/**
 * Where it is safe to send somebody after they sign in.
 *
 * # WHY THIS EXISTS
 *
 * Because the authenticated layout named its own location as the place to come
 * back to, and that location was the login screen.
 *
 * The guard navigates with `search: { redirect: pathname }` and its effect
 * re-runs on every pathname change, so the second firing wrote
 * `?redirect=/sign-in` over the first one's `?redirect=/`. The value was then
 * carried to the second-factor screen, which on success did exactly as it was
 * told and navigated back to the login form. The code had been accepted, the
 * session was complete, and the person was looking at the page they had just
 * submitted — reported as "sometimes I enter the right code and end up back at
 * the login window".
 *
 * It worked on the second try, every time, because by then the person was
 * already on /sign-in, the layout never mounted, and no redirect was set. The
 * audit log records that shape precisely: pairs of successful sign-ins seconds
 * apart, for weeks, with no rejected password and no rejected code between
 * them.
 *
 * So the rule lives in one place and is applied at both ends — where a target
 * is written and where one is read — because a loop needs only one unguarded
 * side to come back.
 */

/** The screens that exist to get somebody signed in. Returning to one of these after signing in is the loop. */
const AUTH_SCREENS = [
  "/sign-in",
  "/two-factor",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/close-account",
];

/**
 * Reports whether a path is somewhere worth returning to.
 *
 * Only in-app paths: a value from a query string must never become an
 * off-site navigation, so anything that does not start with a single slash is
 * refused. "//evil.example" is two slashes and is a protocol-relative URL, not
 * a path.
 */
export function isSafeRedirect(path: string | undefined): path is string {
  if (!path || !path.startsWith("/") || path.startsWith("//")) return false;
  // split always yields at least one element, but the compiler is told so
  // explicitly rather than with a non-null assertion.
  const pathname = path.split(/[?#]/)[0] ?? path;
  return !AUTH_SCREENS.some(
    (screen) => pathname === screen || pathname.startsWith(screen + "/"),
  );
}

/** The redirect to use, or "/" when there is nothing safe to go back to. */
export function safeRedirect(path: string | undefined): string {
  return isSafeRedirect(path) ? path : "/";
}
